import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import BranchPage from "@/pages/BranchPage";
import MemberPage from "@/pages/MemberPage";
import TimelinePage from "@/pages/TimelinePage";
import InvitePage from "@/pages/InvitePage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import HelpPage from "@/pages/HelpPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Legacy: Check for session_id in hash (Emergent auth)
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          throw new Error("Auth failed");
        }

        const user = await response.json();
        navigate("/dashboard", { replace: true, state: { user } });
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Not authenticated");
        }

        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        // Store the current path so we can redirect back after login
        // This is especially important for invite links
        if (location.pathname.startsWith('/invite/')) {
          sessionStorage.setItem('redirectAfterLogin', location.pathname);
        }
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ user, setUser });
};

function AppRouter() {
  const location = useLocation();

  // Legacy: Check for session_id in hash (Emergent auth - kept for backward compatibility)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/branch/:branchId"
        element={
          <ProtectedRoute>
            {({ user }) => <BranchPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/member/:memberId"
        element={
          <ProtectedRoute>
            {({ user }) => <MemberPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/branch/:branchId/timeline"
        element={
          <ProtectedRoute>
            {({ user }) => <TimelinePage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/branch/:branchId/invite"
        element={
          <ProtectedRoute>
            {({ user }) => <InvitePage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/invite/:inviteToken"
        element={
          <ProtectedRoute>
            {({ user }) => <AcceptInvitePage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/help" element={<HelpPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
